// ─────────────────────────────────────────────────────────────────
// Glass-i — Dispatch Plan detail (Stage DISPATCH-PLAN-2).
// A dense, per-line partial-dispatch grid: each work order expands to its
// line items; enter a Dispatch N per line down the tinted lane, mark it
// dispatched (reversible), and the balance carries across shipments. Add
// individual lines or whole work orders via the Add-lines picker.
// PLANNING artifact only — nothing here ships stock or posts to accounts/GST.
// ─────────────────────────────────────────────────────────────────

import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Table, Tag, Typography, Button, Space, message, Descriptions, Alert, Spin,
  Statistic, Row, Col, Modal, Popconfirm, InputNumber, Input, Segmented, Tooltip, Progress
} from 'antd';
import {
  ArrowLeftOutlined, ReloadOutlined, PlusOutlined, DeleteOutlined,
  CheckOutlined, StopOutlined, TruckOutlined, SearchOutlined, CarOutlined, PhoneOutlined,
  AppstoreOutlined, CheckCircleOutlined, HourglassOutlined, PrinterOutlined
} from '@ant-design/icons';

import { dispatchPlanApi } from './dispatchPlanApi';
import { PERM, hasPerm, planStatusTag, vehicleModeTag, fmtWindow, fmtDate, fmtQty, fmtArea, pieceAreaSqm } from './dispatchPlanHelpers';
import useAuth from '../../../hooks/useAuth';
import './dispatchPlanGrid.css';

const { Text } = Typography;
const errMsg = (e, fb) => e?.response?.data?.error?.message || e?.message || fb;

// ── Add-lines picker: window WOs → expand to pick specific lines or a whole WO ──
function AddLinesModal({ open, onClose, plan, onDone }) {
  const [woRows, setWoRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linesByWo, setLinesByWo] = useState({});      // woId -> lines[]
  const [loadingWo, setLoadingWo] = useState({});      // woId -> bool
  const [selByWo, setSelByWo] = useState({});          // woId -> [lineId]

  useEffect(() => {
    if (!open) { setWoRows([]); setLinesByWo({}); setSelByWo({}); return; }
    (async () => {
      setLoading(true);
      try {
        const data = await dispatchPlanApi.suggest({ deliveryDateFrom: plan.deliveryDateFrom, deliveryDateTo: plan.deliveryDateTo });
        setWoRows((data && data.rows) || []);
      } catch (e) { message.error(errMsg(e, 'Could not load work orders.')); }
      finally { setLoading(false); }
    })();
  }, [open, plan]);

  const loadWoLines = useCallback(async (woId) => {
    setLoadingWo((m) => ({ ...m, [woId]: true }));
    try {
      const data = await dispatchPlanApi.candidateLines(plan.id, woId);
      setLinesByWo((m) => ({ ...m, [woId]: (data && data.lines) || [] }));
    } catch (e) { message.error(errMsg(e, 'Could not load lines for this work order.')); }
    finally { setLoadingWo((m) => ({ ...m, [woId]: false })); }
  }, [plan]);

  const totalSelected = useMemo(
    () => Object.values(selByWo).reduce((a, arr) => a + (arr ? arr.length : 0), 0), [selByWo]);

  const addable = (ln) => !ln.onPlan && ln.remaining > 0;

  const save = async () => {
    const ids = Object.values(selByWo).flat();
    if (!ids.length) { message.info('Select at least one line to add.'); return; }
    setSaving(true);
    try {
      const out = await dispatchPlanApi.addLines(plan.id, ids);
      message.success(`${out.linesAdded} line${out.linesAdded === 1 ? '' : 's'} added to the plan.`);
      onClose(); onDone && onDone();
    } catch (e) { message.error(errMsg(e, 'Could not add the lines.')); }
    finally { setSaving(false); }
  };

  const innerColumns = [
    { title: '#', dataIndex: 'lineNo', width: 48, render: (v) => <span className="dpg-num dpg-dim">{v}</span> },
    { title: 'Type', dataIndex: 'type', width: 100, render: (v) => v || '—' },
    { title: 'Item description', dataIndex: 'itemDesc', ellipsis: true, render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Size (mm)', width: 130, render: (_, r) => (r.widthMm != null && r.heightMm != null ? `${fmtQty(r.widthMm)} × ${fmtQty(r.heightMm)}` : '—') },
    { title: 'Ordered', dataIndex: 'ordered', width: 80, align: 'right', render: (v) => <span className="dpg-num">{fmtQty(v)}</span> },
    { title: 'Ready', dataIndex: 'ready', width: 70, align: 'right', render: (v) => <span className="dpg-num" style={{ color: '#157a49' }}>{fmtQty(v)}</span> },
    { title: 'Remaining', dataIndex: 'remaining', width: 90, align: 'right', render: (v) => <span className="dpg-num">{fmtQty(v)}</span> },
    { title: '', width: 96, render: (_, r) => (r.onPlan ? <Tag color="green">On plan</Tag> : (r.remaining <= 0 ? <Tag>Fully sent</Tag> : null)) }
  ];

  const woColumns = [
    { title: 'Work order', dataIndex: 'woNo', width: 150, render: (v) => <code className="dpg-mono">{v}</code> },
    { title: 'Customer', dataIndex: 'customerName', render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Delivery', dataIndex: 'deliveryDate', width: 130, render: (v) => fmtDate(v) },
    { title: 'Remaining', dataIndex: 'remainingQty', width: 100, align: 'right', render: (v, r) => (r.fullyDispatched ? <Tag>Fully sent</Tag> : <span className="dpg-num">{fmtQty(v)}</span>) }
  ];

  return (
    <Modal open={open} onCancel={onClose} onOk={save} okText={totalSelected ? `Add ${totalSelected} to plan` : 'Add to plan'}
      okButtonProps={{ disabled: totalSelected === 0, loading: saving }} title="Add work orders / lines" width={900} destroyOnClose>
      <Table
        rowKey="workOrderId" size="small" columns={woColumns} dataSource={woRows} loading={loading} pagination={{ pageSize: 8, size: 'small' }}
        locale={{ emptyText: 'No committed work orders in this window' }}
        expandable={{
          onExpand: (expanded, r) => { if (expanded && !linesByWo[r.workOrderId]) loadWoLines(r.workOrderId); },
          expandedRowRender: (r) => {
            const lines = linesByWo[r.workOrderId] || [];
            const selectable = lines.filter(addable).map((l) => l.workOrderLineId);
            return (
              <Table
                rowKey="workOrderLineId" size="small" columns={innerColumns} dataSource={lines}
                loading={!!loadingWo[r.workOrderId]} pagination={false} scroll={{ y: 240 }}
                locale={{ emptyText: 'No dispatchable lines' }}
                rowSelection={{
                  selectedRowKeys: selByWo[r.workOrderId] || [],
                  getCheckboxProps: (ln) => ({ disabled: !addable(ln) }),
                  onChange: (keys) => setSelByWo((m) => ({ ...m, [r.workOrderId]: keys })),
                  // "select whole WO" = the header checkbox selects all addable lines
                  onSelectAll: (checked) => setSelByWo((m) => ({ ...m, [r.workOrderId]: checked ? selectable : [] }))
                }}
              />
            );
          }
        }}
      />
      <Text type="secondary" style={{ fontSize: 12 }}>Tick individual lines, or use a line-table header checkbox to take a whole work order.</Text>
    </Modal>
  );
}

// ── one editable line row (memoized so typing in one cell doesn't re-render all) ──
// Planning only: set the pieces to dispatch (N) per line. Marking a line actually
// dispatched happens automatically later (at tax invoice / delivery challan).
function LineRow({ line, editable, onCommitQty, onSetReady, onRemove }) {
  const [val, setVal] = useState(line.dispatchQty);
  useEffect(() => { setVal(line.dispatchQty); }, [line.dispatchQty]);
  const disabled = !editable || line.ordered === 0;

  const commit = () => {
    const q = Number.isFinite(val) ? Math.floor(val) : 0;
    if (q !== line.dispatchQty) onCommitQty(line, q);
  };
  const onEnter = (e) => {
    commit();
    const inputs = [...document.querySelectorAll('.dpg-scroll .dpg-nin input:not([disabled])')];
    const i = inputs.indexOf(e.target);
    if (inputs[i + 1]) inputs[i + 1].focus();
  };

  return (
    <tr className="dpg-row" data-lineid={line.lineId}>
      <td className="dpg-no dpg-num">{line.lineNo}</td>
      <td className="dpg-type">{line.type || '—'}</td>
      <td className="dpg-desc" title={line.itemDesc || ''}>{line.itemDesc || '—'}</td>
      <td className="r dpg-num">{line.widthMm != null ? fmtQty(line.widthMm) : '—'}</td>
      <td className="r dpg-num">{line.heightMm != null ? fmtQty(line.heightMm) : '—'}</td>
      <td className="r dpg-num">{fmtQty(line.ordered)}</td>
      <td className="r dpg-num dpg-rdy">{fmtQty(line.ready)}</td>
      <td className={`r dpg-lane${line.readyShort ? ' dpg-short' : ''}`}>
        <span className="dpg-nin">
          <InputNumber
            size="small" min={0} max={line.ordered} step={1} precision={0} controls={false}
            value={val} disabled={disabled} onChange={setVal} onBlur={commit} onPressEnter={onEnter}
            title={line.readyShort ? `Planning ${line.dispatchQty}, only ${line.ready} produced so far` : `Max ${line.ordered}`}
          />
        </span>
        {editable && line.ordered > 0
          ? <button type="button" className="dpg-eq" title={`Set N = ready (${line.ready})`} onClick={() => onSetReady(line)}>=</button>
          : null}
      </td>
      <td className={`r dpg-bal dpg-num${line.balanceQty === 0 ? ' dpg-zero' : ''}`}><b>{fmtQty(line.balanceQty)}</b></td>
      <td className="c">
        {editable ? (
          <Popconfirm title="Remove this line from the plan?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => onRemove(line)}>
            <Button size="small" type="text" danger className="dpg-del" icon={<DeleteOutlined />}
              title="Remove line from plan" aria-label="Remove line from plan" />
          </Popconfirm>
        ) : null}
      </td>
    </tr>
  );
}
const MemoLineRow = memo(LineRow);

// Short "12 Aug, 14:20" stamp (en-IN) for the "last recorded" line.
function fmtStamp(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// The vehicle detail line for an arranged WO (no · contact · remarks).
function vehicleDetail(v) {
  const bits = [];
  if (v.no) bits.push(<span key="n" className="dpg-vno">{v.no}</span>);
  if (v.contact) bits.push(<span key="c">☎ {v.contact}</span>);
  if (v.remarks) bits.push(<span key="r" className="dpg-rmk">“{v.remarks}”</span>);
  if (!bits.length) return 'Arranged';
  const out = [];
  bits.forEach((el, i) => { if (i) out.push(<span key={`s${i}`} style={{ opacity: 0.5 }}> · </span>); out.push(el); });
  return out;
}

// ── record how a work order will ship: customer's own vehicle vs company-arranged ──
function VehicleModal({ open, item, planId, onClose, onSaved }) {
  const [mode, setMode] = useState('PENDING');       // PENDING | CUSTOMER | COMPANY
  const [no, setNo] = useState('');
  const [contact, setContact] = useState('');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    const v = item.vehicle || {};
    setMode(v.mode || 'PENDING'); setNo(v.no || ''); setContact(v.contact || ''); setRemarks(v.remarks || '');
  }, [open, item]);

  const pending = mode === 'PENDING';
  const save = async () => {
    setSaving(true);
    try {
      const payload = { mode: pending ? null : mode, vehicleNo: no, vehicleContact: contact, remarks };
      const out = await dispatchPlanApi.setItemVehicle(planId, item.id, payload);
      message.success(pending ? 'Reset to Pending.' : `Vehicle arrangement saved for ${item.woNo}.`);
      onSaved(item.id, out.vehicle); onClose();
    } catch (e) { message.error(errMsg(e, 'Could not save the vehicle arrangement.')); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onCancel={onClose} onOk={save} okText="Save arrangement" confirmLoading={saving}
      title={<Space><CarOutlined />Record vehicle arrangement</Space>} width={520} destroyOnClose>
      {item ? <Text type="secondary" style={{ fontSize: 12 }}>{item.woNo} · {item.customerName || '—'}</Text> : null}
      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>How will this order ship?</span>
          {!pending ? <Button size="small" type="link" style={{ padding: 0, height: 'auto', fontSize: 12 }}
            onClick={() => { setMode('PENDING'); setNo(''); setContact(''); setRemarks(''); }}>Reset to Pending</Button> : null}
        </div>
        <Segmented block value={mode} onChange={setMode}
          options={[{ label: 'Pending', value: 'PENDING' }, { label: 'Customer vehicle', value: 'CUSTOMER' }, { label: 'Company arranged', value: 'COMPANY' }]} />
      </div>
      <Row gutter={12} style={{ marginTop: 14 }}>
        <Col span={12}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Vehicle number <Text type="secondary" style={{ fontWeight: 400 }}>(optional)</Text></div>
          <Input value={no} onChange={(e) => setNo(e.target.value)} disabled={pending} maxLength={32} placeholder="e.g. TN 09 AB 1234" />
        </Col>
        <Col span={12}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Driver / contact phone <Text type="secondary" style={{ fontWeight: 400 }}>(optional)</Text></div>
          <Input value={contact} onChange={(e) => setContact(e.target.value)} disabled={pending} maxLength={32} placeholder="e.g. 98765 43210" />
        </Col>
      </Row>
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Remarks <Text type="secondary" style={{ fontWeight: 400 }}>(free text)</Text></div>
        <Input.TextArea value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={pending}
          rows={3} maxLength={1000} showCount placeholder="e.g. Company tempo, pickup 10am from unit-2 gate. Call before dispatch." />
      </div>
    </Modal>
  );
}

export default function DispatchPlanDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const canUpdate = hasPerm(me, PERM.UPDATE);

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState({});      // itemId -> bool
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [vehItem, setVehItem] = useState(null);        // WO item whose vehicle modal is open
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try { setPlan(await dispatchPlanApi.get(id)); }
    catch (e) { setPlan(null); setLoadError(errMsg(e, 'Could not load this plan.')); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const editable = plan && plan.status !== 'CANCELLED' && canUpdate;

  // Immutable per-line patch — keeps unchanged line identities so memoized rows skip.
  const patchLine = useCallback((lineId, patch) => {
    setPlan((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((it) => {
          if (!it.lines.some((l) => l.lineId === lineId)) return it;
          return { ...it, lines: it.lines.map((l) => (l.lineId === lineId ? { ...l, ...patch } : l)) };
        })
      };
    });
  }, []);

  // Patch one WO item in place (vehicle arrangement) + recompute the plan's
  // arranged/pending rollup so the header stat updates immediately.
  const patchItem = useCallback((itemId, patch) => {
    setPlan((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it));
      const arranged = items.filter((i) => i.vehicle && i.vehicle.mode).length;
      return { ...prev, items, vehicle: { arranged, pending: items.length - arranged, total: items.length } };
    });
  }, []);

  const commitQty = useCallback(async (line, qty) => {
    try {
      const out = await dispatchPlanApi.updateItemLine(id, line.lineId, { dispatchQty: qty });
      patchLine(line.lineId, out);
    } catch (e) { message.error(errMsg(e, 'Could not update the quantity.')); load(); }
  }, [id, patchLine, load]);

  const setReady = useCallback((line) => {
    commitQty(line, Math.min(line.ready, line.ordered));
  }, [commitQty]);

  const removeLine = useCallback(async (line) => {
    try { await dispatchPlanApi.removeLine(id, line.lineId); message.success('Line removed from the plan.'); load(); }
    catch (e) { message.error(errMsg(e, 'Could not remove the line.')); }
  }, [id, load]);

  const setStatus = useCallback(async (status) => {
    setBusy(true);
    try { await dispatchPlanApi.update(id, { status }); message.success(`Plan ${status.toLowerCase()}.`); load(); }
    catch (e) { message.error(errMsg(e, 'Could not update the plan.')); }
    finally { setBusy(false); }
  }, [id, load]);

  // ── filtering (client-side over the plan's bounded line set) ──
  const q = search.trim().toLowerCase();
  const lineVisible = useCallback((l) => {
    if (q) {
      const t = `${l.type || ''} ${l.itemDesc || ''} ${l.widthMm}x${l.heightMm}`.toLowerCase();
      if (!t.includes(q)) return false;
    }
    if (filter === 'ready') return l.ready > 0;
    return true;
  }, [q, filter]);

  const totals = useMemo(() => {
    const t = { ordered: 0, ready: 0, toDispatch: 0, balance: 0, area: 0 };
    if (!plan) return t;
    for (const it of plan.items) for (const l of it.lines) {
      t.ordered += l.ordered; t.ready += l.ready; t.balance += l.balanceQty;
      t.toDispatch += l.dispatchQty;
      t.area += pieceAreaSqm(l.widthMm, l.heightMm) * l.dispatchQty;
    }
    return t;
  }, [plan]);

  // Bulk over the visible on-plan lines.
  const bulk = useCallback(async (kind) => {
    if (!plan) return;
    const items = [];
    for (const it of plan.items) for (const l of it.lines) {
      if (!lineVisible(l)) continue;
      if (kind === 'fill') items.push({ lineId: l.lineId, dispatchQty: Math.min(l.ready, l.ordered) });
      else if (kind === 'clear') items.push({ lineId: l.lineId, dispatchQty: 0 });
    }
    if (!items.length) { message.info('No visible line matches that action.'); return; }
    setBusy(true);
    try { await dispatchPlanApi.bulkApplyLines(id, items); load(); }
    catch (e) { message.error(errMsg(e, 'Bulk action failed.')); }
    finally { setBusy(false); }
  }, [plan, id, lineVisible, load]);

  const groups = useMemo(() => {
    if (!plan) return [];
    return plan.items.map((it) => ({ item: it, lines: it.lines.filter(lineVisible) })).filter((g) => g.lines.length > 0);
  }, [plan, lineVisible]);

  const veh = (plan && plan.vehicle) || { arranged: 0, pending: plan ? plan.items.length : 0, total: plan ? plan.items.length : 0 };
  const readyPct = totals.ordered > 0 ? Math.min(100, Math.round((totals.ready / totals.ordered) * 100)) : 0;

  return (
    <div className="dpg-page">
      {loadError ? <Alert type="error" showIcon message={loadError} className="mb-2" /> : null}
      {loading && !plan ? <div className="py-8 text-center"><Spin /></div> : null}

      {plan ? (
        <>
          <Card size="small" className="dpg-headcard mb-2" styles={{ body: { padding: 14 } }}>
            <div className="dpg-head">
              <div className="dpg-head-id">
                <Button size="small" type="text" className="dpg-back" icon={<ArrowLeftOutlined />}
                  onClick={() => navigate('/erp/dispatch/plans')}>Back</Button>
                <TruckOutlined className="dpg-head-truck" />
                <span className="dpg-eyebrow">Dispatch plan</span>
                <code className="dpg-mono dpg-head-no">{plan.planNo}</code>
                {(() => { const t = planStatusTag(plan.status); return <Tag color={t.color}>{t.label}</Tag>; })()}
                <span className="dpg-head-ctx">
                  {plan.planName ? `${plan.planName} · ` : ''}
                  Delivery {fmtWindow(plan.deliveryDateFrom, plan.deliveryDateTo)} · {fmtQty(plan.itemsCount)} work order{plan.itemsCount === 1 ? '' : 's'}
                </span>
              </div>
              <div className="dpg-head-actions">
                {editable ? (
                  <>
                    <Button size="small" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Add work orders / lines</Button>
                    {plan.status === 'DRAFT' ? <Button size="small" type="primary" icon={<CheckOutlined />} loading={busy} onClick={() => setStatus('CONFIRMED')}>Confirm</Button> : null}
                    <Popconfirm title="Cancel this plan?" okText="Cancel plan" okButtonProps={{ danger: true }} onConfirm={() => setStatus('CANCELLED')}>
                      <Button size="small" danger icon={<StopOutlined />}>Cancel</Button>
                    </Popconfirm>
                  </>
                ) : null}
                <Tooltip title={totals.toDispatch > 0 ? 'Print the A4 vehicle loading slip' : 'Set a Dispatch N on at least one line first'}>
                  <Button size="small" icon={<PrinterOutlined />} disabled={totals.toDispatch === 0}
                    onClick={() => navigate(`/erp/dispatch/plans/${id}/loading-slip`)}>Loading slip</Button>
                </Tooltip>
                <Tooltip title="Refresh">
                  <Button size="small" type="text" icon={<ReloadOutlined />} loading={loading} onClick={load} aria-label="Refresh" />
                </Tooltip>
              </div>
            </div>
            <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} colon={false} className="dpg-meta"
              items={[
                { key: 'name', label: 'Name', children: plan.planName || '—' },
                { key: 'win', label: 'Delivery window', children: fmtWindow(plan.deliveryDateFrom, plan.deliveryDateTo) },
                { key: 'cust', label: 'Customer', children: <span title={plan.customerName || 'All'}>{plan.customerName || 'All'}</span> },
                { key: 'route', label: 'Route', children: plan.deliveryRoute || '—' },
                { key: 'wos', label: 'Work orders', children: <span className="dpg-num">{fmtQty(plan.itemsCount)}</span> },
                { key: 'created', label: 'Created', children: fmtDate(plan.createdAt) },
                { key: 'rem', label: 'Remarks', span: 3, children: plan.remarks || '—' }
              ]} />
          </Card>

          <div className="dpg-kpis mb-2">
            <div className="dpg-kpi">
              <div className="dpg-kpi-top"><span className="dpg-kpi-label">Ordered</span><AppstoreOutlined className="dpg-kpi-ic" /></div>
              <Statistic value={totals.ordered} valueStyle={{ fontSize: 22, lineHeight: 1.15 }} />
              <div className="dpg-kpi-sub">Total pieces</div>
            </div>
            <div className="dpg-kpi dpg-kpi-ok">
              <div className="dpg-kpi-top"><span className="dpg-kpi-label">Ready</span><CheckCircleOutlined className="dpg-kpi-ic" /></div>
              <Statistic value={totals.ready} valueStyle={{ fontSize: 22, lineHeight: 1.15, color: '#157a49' }}
                suffix={<span className="dpg-kpi-pct">{readyPct}%</span>} />
              <Progress percent={readyPct} showInfo={false} size="small" strokeColor="#52a447" trailColor="#e7efe4" />
            </div>
            <div className="dpg-kpi dpg-kpi-info">
              <div className="dpg-kpi-top"><span className="dpg-kpi-label">To dispatch</span><TruckOutlined className="dpg-kpi-ic" /></div>
              <Statistic value={totals.toDispatch} valueStyle={{ fontSize: 22, lineHeight: 1.15, color: '#1c4e80' }} />
              <div className="dpg-kpi-sub">Staged to load</div>
            </div>
            <div className="dpg-kpi dpg-kpi-warn">
              <div className="dpg-kpi-top"><span className="dpg-kpi-label">Balance</span><HourglassOutlined className="dpg-kpi-ic" /></div>
              <Statistic value={totals.balance} valueStyle={{ fontSize: 22, lineHeight: 1.15, color: totals.balance > 0 ? '#a3590a' : '#157a49' }}
                suffix={<span className="dpg-kpi-area">· {fmtArea(totals.area)} m²</span>} />
              <div className="dpg-kpi-sub">Remaining</div>
            </div>
            <div className="dpg-kpi dpg-kpi-veh">
              <div className="dpg-kpi-top"><span className="dpg-kpi-label">Vehicle arranged</span><CarOutlined className="dpg-kpi-ic" /></div>
              <Statistic value={veh.arranged} valueStyle={{ fontSize: 22, lineHeight: 1.15, color: veh.pending > 0 ? '#a3590a' : '#157a49' }}
                suffix={<span className="dpg-kpi-of">/ {fmtQty(veh.total)}</span>} />
              {veh.pending > 0
                ? <Tag color="gold" className="dpg-kpi-tag">{fmtQty(veh.pending)} pending</Tag>
                : <Tag color="green" className="dpg-kpi-tag">All arranged</Tag>}
            </div>
          </div>

          <div className="dpg-workspace mb-2">
            <div className="dpg-toolbar">
              <div className="dpg-toolbar-left">
                <Input allowClear prefix={<SearchOutlined />} placeholder="Search line, size…" value={search}
                  onChange={(e) => setSearch(e.target.value)} className="dpg-search" size="small" />
                <Segmented size="small" value={filter} onChange={setFilter}
                  options={[{ label: 'All lines', value: 'all' }, { label: 'Has ready pieces', value: 'ready' }]} />
              </div>
              {editable ? (
                <div className="dpg-toolbar-right">
                  <Tooltip title="Set each visible line's Dispatch N to its ready-piece count">
                    <Button size="small" onClick={() => bulk('fill')} loading={busy}>Set N = ready</Button>
                  </Tooltip>
                  <Tooltip title="Clear the Dispatch N on every visible line">
                    <Button size="small" onClick={() => bulk('clear')} loading={busy}>Clear N</Button>
                  </Tooltip>
                </div>
              ) : null}
            </div>
            <div className="dpg-wrap">
              <div className="dpg-scroll" ref={scrollRef}>
              <table className="dpg-table">
                <thead>
                  <tr>
                    <th className="dpg-no">#</th><th>Type</th><th>Item description</th>
                    <th className="r">Act W</th><th className="r">Act H</th>
                    <th className="r">Ordered</th><th className="r">Rdy</th>
                    <th className="r dpg-lane">Dispatch N</th><th className="r">Balance</th>
                    <th className="c"> </th>
                  </tr>
                </thead>
                <tbody>
                  {groups.length === 0 ? (
                    <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#69747f' }}>
                      No lines on this plan{plan.items.length ? ' match this view' : ''}. Use “Add work orders / lines”.
                    </td></tr>
                  ) : groups.map(({ item, lines }) => {
                    const isCol = !!collapsed[item.id];
                    const v = item.vehicle || {};
                    const pendingVeh = !v.mode;
                    const vt = vehicleModeTag(v.mode);
                    return (
                      <Fragment key={`g-${item.id}`}>
                        <tr className={`dpg-grp${pendingVeh ? ' dpg-grppending' : ''}`}>
                          <td colSpan={10}>
                            <div className="dpg-gwrap">
                              <span className="dpg-gclick" onClick={() => setCollapsed((m) => ({ ...m, [item.id]: !m[item.id] }))}>
                                <span className="dpg-caret">{isCol ? '▸' : '▾'}</span>
                                <span className="dpg-wono dpg-mono">{item.woNo}</span>
                                <span className="dpg-dim" style={{ fontSize: 11 }}>{item.lineCount} lines · del {fmtDate(item.deliveryDate)}</span>
                                {item.overdue ? <Tag color="red" style={{ marginLeft: 6 }}>Overdue</Tag> : null}
                              </span>
                              <span style={{ flex: 1 }} />
                              <span className="dpg-gtally">
                                <span>Ordered <b className="dpg-num">{fmtQty(item.ordered)}</b></span>
                                <span>Ready <b className="dpg-num" style={{ color: '#157a49' }}>{fmtQty(item.ready)}</b></span>
                                <span>To dispatch <b className="dpg-num" style={{ color: '#1c4e80' }}>{fmtQty(item.toDispatch)}</b></span>
                                <span>Balance <b className="dpg-num" style={{ color: item.balance > 0 ? '#a3590a' : '#157a49' }}>{fmtQty(item.balance)}</b></span>
                              </span>
                            </div>
                          </td>
                        </tr>
                        <tr className={`dpg-vehrow${pendingVeh ? ' dpg-vehpending' : ''}`}>
                          <td colSpan={10}>
                            <div className="dpg-veh">
                              <div className="dpg-veh-left">
                                <Tag color={vt.color} style={{ marginRight: 0 }}>{vt.label}</Tag>
                                {pendingVeh
                                  ? <span className="dpg-veh-nudge"><PhoneOutlined /> Call customer to arrange vehicle</span>
                                  : <span className="dpg-veh-detail">{vehicleDetail(v)}</span>}
                                {v.updatedAt ? <span className="dpg-veh-stamp">{v.updatedBy ? `${v.updatedBy} · ` : ''}{fmtStamp(v.updatedAt)}</span> : null}
                              </div>
                              {editable ? (
                                <Button size="small" type={pendingVeh ? 'primary' : 'default'} icon={<CarOutlined />} onClick={() => setVehItem(item)}>
                                  {pendingVeh ? 'Record vehicle' : 'Edit'}
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                        {isCol ? null : lines.map((l) => (
                          <MemoLineRow key={l.lineId} line={l} editable={editable}
                            onCommitQty={commitQty} onSetReady={setReady} onRemove={removeLine} />
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5} className="dpg-flabel">Totals · on-plan lines</td>
                    <td className="r dpg-num">{fmtQty(totals.ordered)}</td>
                    <td className="r dpg-num">{fmtQty(totals.ready)}</td>
                    <td className="r dpg-lane dpg-num">{fmtQty(totals.toDispatch)}</td>
                    <td className="r dpg-num">{fmtQty(totals.balance)}</td>
                    <td className="c"> </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            </div>
            <div className="dpg-hint">
              <Text type="secondary" style={{ fontSize: 11 }}>
                Planning only — set the pieces to dispatch (N) per line; press Enter to jump to the next line. Lines are marked dispatched automatically later, when the tax invoice / delivery challan is raised. Areas at 3 dp (en-IN).
              </Text>
            </div>
          </div>

          <AddLinesModal open={addOpen} onClose={() => setAddOpen(false)} plan={plan} onDone={load} />
          <VehicleModal open={!!vehItem} item={vehItem} planId={id}
            onClose={() => setVehItem(null)} onSaved={(itemId, vehicle) => patchItem(itemId, { vehicle })} />
        </>
      ) : null}
    </div>
  );
}
